import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { InlineManualTracking, Tutorial, TutorialViewState, UserTutorialMap, UserTutorials } from '../models/tutorial.model';
import { WindowRef } from './window.service';

@Injectable({
    providedIn: 'root',
})
export class TutorialService {
    usersTutorials: UserTutorials | undefined;
    readonly API_URL: string = '/api/customer_support/tutorialviewstates';
    private readonly TUTORIAL_BASE_URL: string = '/api/customer_support/tutorials';

    constructor(
        private httpClient: HttpClient,
        private windowRef: WindowRef,
    ) {}

    getUsersTutorials$(userId: string): Observable<UserTutorials> {
        return this.httpClient.get<UserTutorials>(this.API_URL + '/' + userId);
    }

    getTutorials$(): Observable<Tutorial[]> {
        return this.httpClient.get<Tutorial[]>(this.TUTORIAL_BASE_URL);
    }

    postTutorial$(tutorial: Tutorial): Observable<Object> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };
        return this.httpClient.post(this.TUTORIAL_BASE_URL, tutorial, httpOptions);
    }

    removeTutorial$(id: string): Observable<Object> {
        return this.httpClient.delete(this.TUTORIAL_BASE_URL + '/' + id);
    }

    checkIfTutorialAlreadyViewed(userId: string, tutorialRef: string): void {
        this.getUsersTutorials$(userId)
            .pipe(
                map((data: UserTutorials): void => {
                    this.usersTutorials = data;
                    if (this.usersTutorials.visitedTutorials != null) {
                        const tutorial: TutorialViewState | undefined = this.usersTutorials.visitedTutorials.find(
                            (mappedTutorial: TutorialViewState): boolean => mappedTutorial.externalId === tutorialRef,
                        );
                        if (tutorial != null && tutorial.viewedAt == null && tutorial.tutorialId) {
                            const tutorialViewState: UserTutorialMap = {
                                userId: userId,
                                tutorialId: tutorial.tutorialId,
                            };
                            this.updatePlayer();
                            this.activateTopic(tutorialRef);
                            this.httpClient.post(this.API_URL, tutorialViewState).subscribe();
                        }
                    }
                }),
            )
            .subscribe();
    }
    setTracking(trackingData: InlineManualTracking): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.windowRef.nativeWindow as any).inlineManualTracking = trackingData;
    }

    createPlayer(): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const trackingData: InlineManualTracking | undefined = (this.windowRef.nativeWindow as any).inlineManualPlayerData || undefined;
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.windowRef.nativeWindow as any).createInlineManualPlayer(trackingData);
        } catch (error) {
            console.error(error);
        }
    }

    activateTopic(id: string): void {
        // Access the inline_manual_player from the window object
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const inlineManualPlayer = (this.windowRef.nativeWindow as any).inline_manual_player;

        // Check if the inline_manual_player is defined
        if (!inlineManualPlayer) {
            console.error('inline_manual_player is not defined.');
            return;
        }
        try {
            // Call the activateTopic method on the inline_manual_player
            inlineManualPlayer.activateTopic(id).then(
                (result: Error): void => {
                    console.log('callback: ', result);
                },

                (error: Error): void => {
                    console.log('fail: ', error);
                },
            );
        } catch (error) {
            console.error('Error activating topic:', error.message);
        }
    }

    updatePlayer(): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!(this.windowRef.nativeWindow as any).inline_manual_player) {
            return;
        }
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.windowRef.nativeWindow as any).inline_manual_player.update();
        } catch (error) {
            console.error(error);
        }
    }
}

/**
 * imAddTopicParam
 * Must be defined on window-level, because IM needs to have access to it.
 * Method is used when a tutorial requests you to click on the Arivis player. There, on the link to Arivis
 * we need to append a URL parameter with the topic ID to pass it to Arivis.
 */
(
    window as unknown as Window & {
        IM: { imAddTopicParam: (_player_id: string, _topic_id: string, _step_id: string, custom_data: { element: Element | string; topicId: string }) => void };
    }
).IM = {
    imAddTopicParam: (_player_id, _topic_id, _step_id, custom_data): void => {
        // custom_data.topicId - topicId we want to append to URL
        // custom_data.element - element we want to search for and change href for
        const el = document.querySelector(custom_data.element as string);
        if (el != null) {
            const url = new URL(el.getAttribute('href') as string, window.location.origin); // you might need to add a full URL in case it is a relative path
            url.searchParams.append('imTopic', custom_data.topicId);

            el.setAttribute('href', url as unknown as string);
        }
    },
};
